/**
 * Cold-start retry primitives for Firebase callable functions.
 *
 * ADR-0039 — Cold-Start Retry Policy:
 *   READ policy:     retries on functions/internal, functions/unavailable,
 *                    functions/deadline-exceeded, functions/cancelled,
 *                    functions/aborted, functions/resource-exhausted,
 *                    functions/unknown, and RN network/timeout errors.
 *                    Budget 25 s, initial delay 600 ms, max delay 4 s.
 *
 *   MUTATION policy: retries NOTHING. There is no client-visible error code
 *                    that safely proves a mutation never ran: a backend handler
 *                    can throw HttpsError("unavailable") from inside itself
 *                    (e.g. mapping a downstream 5xx) AFTER a side-effect, which
 *                    is indistinguishable from an infrastructure 503. Retrying
 *                    would risk double-execution (e.g. a double clock-in). This
 *                    wrapper is the single, safe seam for mutation retry once
 *                    backend idempotency keys exist; until then it is a no-retry
 *                    pass-through that marks "do not cold-start-retry this call".
 */

export type RetryPolicy = "read" | "mutation"
export type RetryDefaults = { totalBudgetMs: number; initialDelayMs: number; maxDelayMs: number }

const READ_RETRYABLE_CODES = new Set([
  "functions/internal",
  "functions/unavailable",
  "functions/deadline-exceeded",
  "functions/cancelled",
  "functions/aborted",
  "functions/resource-exhausted",
  "functions/unknown",
])
// Empty by design — see the MUTATION policy note above. A mutation must not be
// retried on any client-visible code (incl. functions/unavailable), because none
// proves the handler did not already run. (ADR-0039)
const MUTATION_RETRYABLE_CODES = new Set<string>()

export function isRetryableError(err: unknown, policy: RetryPolicy): boolean {
  if (!err || typeof err !== "object") return false
  const codes = policy === "read" ? READ_RETRYABLE_CODES : MUTATION_RETRYABLE_CODES
  const code = (err as { code?: string }).code
  if (typeof code === "string" && codes.has(code)) return true
  if (policy === "read") {
    const msg = (err as { message?: string }).message ?? ""
    if (/network request failed|network error|timeout/i.test(msg)) return true
  }
  return false
}

export async function runWithRetry<T>(
  action: () => Promise<T>,
  policy: RetryPolicy,
  defaults: RetryDefaults,
): Promise<T> {
  const start = Date.now()
  let delay = defaults.initialDelayMs
  while (true) {
    try {
      return await action()
    } catch (err) {
      const remaining = defaults.totalBudgetMs - (Date.now() - start)
      if (!isRetryableError(err, policy) || remaining <= 0) throw err
      await new Promise((r) => setTimeout(r, Math.min(delay, remaining)))
      delay = Math.min(delay * 2, defaults.maxDelayMs)
    }
  }
}

export const READ_DEFAULTS: RetryDefaults = {
  totalBudgetMs: 25_000,
  initialDelayMs: 600,
  maxDelayMs: 4_000,
}
export const MUTATION_DEFAULTS: RetryDefaults = {
  totalBudgetMs: 9_000,
  initialDelayMs: 500,
  maxDelayMs: 3_000,
}
